import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Phone, Users, Calendar, Star, Send, TrendingUp,
  Upload, Download, Settings, LogOut, FileSpreadsheet,
  CheckCircle2, AlertTriangle, XCircle, BarChart3, Bell, Clock,
  FileDown, Loader2, UserPlus, Trash2, Edit2, Eye, EyeOff, Shield, Headphones, Briefcase, Building2, Check, Mail, CalendarOff, Plus
} from "lucide-react";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [settings, setSettings] = useState({ google_review_link: "" });
  const [loading, setLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [googleReviewLink, setGoogleReviewLink] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [pendingReminders, setPendingReminders] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [exporting, setExporting] = useState({ customers: false, calls: false, appointments: false });
  const fileInputRef = useRef(null);
  
  // User management states
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "", company_ids: [], send_email: true });
  const [showPassword, setShowPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  
  // Company management states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: "", google_review_link: "", color: "#4f46e5" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [deletingCompanyId, setDeletingCompanyId] = useState(null);
  
  // Unavailability management states
  const [unavailabilities, setUnavailabilities] = useState([]);
  const [showUnavailModal, setShowUnavailModal] = useState(false);
  const [newUnavail, setNewUnavail] = useState({ user_id: "", start_date: null, end_date: null, reason: "", notes: "" });
  const [savingUnavail, setSavingUnavail] = useState(false);
  
  // Import state
  const [selectedImportCompany, setSelectedImportCompany] = useState("");

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, callsRes, settingsRes, remindersRes, usersRes, companiesRes, unavailRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-calls`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/appointments/upcoming-reminders`),
        axios.get(`${API}/users`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/unavailabilities`)
      ]);
      setStats(statsRes.data);
      setRecentCalls(callsRes.data);
      setSettings(settingsRes.data);
      setGoogleReviewLink(settingsRes.data.google_review_link || "");
      setPendingReminders(remindersRes.data);
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setUnavailabilities(unavailRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/settings?google_review_link=${encodeURIComponent(googleReviewLink)}`);
      toast.success("Paramètres enregistrés");
      setShowSettingsModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!selectedImportCompany) {
      toast.error("Veuillez sélectionner une société avant d'importer");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/import/customers?company_id=${selectedImportCompany}`, 
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult({
        ...response.data,
        companyName: companies.find(c => c.id === selectedImportCompany)?.name
      });
      toast.success(`${response.data.imported} clients importés avec succès`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent = "nom,telephone,email,ville,adresse,type_travaux,date_bdc,somme_ca,origine,commercial\nJean Dupont,0612345678,jean@email.com,Paris,12 rue de la Paix,Rénovation cuisine,2024-01-15,5000,Publicité Facebook,Martin Pierre";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "template_import_clients.csv";
    link.click();
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/appointments/send-reminders`);
      toast.success(`Rappels envoyés: ${response.data.reminders_sent["24h"]} (24h) + ${response.data.reminders_sent["1h"]} (1h)`);
      fetchDashboardData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi des rappels");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleExport = async (type) => {
    setExporting((prev) => ({ ...prev, [type]: true }));
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        responseType: "blob"
      });
      
      // Extract filename from Content-Disposition header or create default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `${type}_export.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${type === "customers" ? "clients" : type === "calls" ? "appels" : "rendez-vous"} téléchargé`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Aucune donnée à exporter");
      } else {
        toast.error("Erreur lors de l'export");
      }
    } finally {
      setExporting((prev) => ({ ...prev, [type]: false }));
    }
  };

  // User management functions
  const openCreateUserModal = () => {
    setEditingUser(null);
    setNewUser({ name: "", email: "", password: "", role: "", company_ids: [], send_email: true });
    setShowPassword(false);
    setShowUserModal(true);
  };

  const openEditUserModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setNewUser({ 
      name: userToEdit.name, 
      email: userToEdit.email, 
      password: "", 
      role: userToEdit.role,
      company_ids: userToEdit.company_ids || [],
      send_email: false  // Don't send email when editing
    });
    setShowPassword(false);
    setShowUserModal(true);
  };

  const handleCompanyToggle = (companyId) => {
    const role = newUser.role;
    if (role === "sales_rep") {
      // Sales rep: only one company allowed
      setNewUser({ ...newUser, company_ids: [companyId] });
    } else {
      // Teleprospector: multiple companies allowed
      const currentIds = newUser.company_ids || [];
      if (currentIds.includes(companyId)) {
        setNewUser({ ...newUser, company_ids: currentIds.filter(id => id !== companyId) });
      } else {
        setNewUser({ ...newUser, company_ids: [...currentIds, companyId] });
      }
    }
  };

  const handleSaveUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.role) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    if (!editingUser && !newUser.password) {
      toast.error("Le mot de passe est obligatoire pour un nouvel utilisateur");
      return;
    }

    // Validate company selection for non-admin roles
    if (newUser.role !== "admin" && (!newUser.company_ids || newUser.company_ids.length === 0)) {
      toast.error("Veuillez sélectionner au moins une société");
      return;
    }

    setSavingUser(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: newUser.name,
          role: newUser.role,
          company_ids: newUser.role === "admin" ? [] : newUser.company_ids
        };
        if (newUser.password) {
          updateData.password = newUser.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success("Utilisateur mis à jour");
      } else {
        // Create new user
        const userData = {
          ...newUser,
          company_ids: newUser.role === "admin" ? [] : newUser.company_ids
        };
        const response = await axios.post(`${API}/users`, userData);
        if (response.data.email_sent) {
          toast.success("Utilisateur créé et email envoyé !");
        } else {
          toast.success("Utilisateur créé avec succès");
        }
      }
      setShowUserModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      return;
    }

    setDeletingUserId(userId);
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilisateur supprimé");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingUserId(null);
    }
  };

  // Company management functions
  const openCreateCompanyModal = () => {
    setEditingCompany(null);
    setNewCompany({ name: "", google_review_link: "", color: "#4f46e5" });
    setShowCompanyModal(true);
  };

  const openEditCompanyModal = (company) => {
    setEditingCompany(company);
    setNewCompany({ 
      name: company.name, 
      google_review_link: company.google_review_link || "",
      color: company.color || "#4f46e5"
    });
    setShowCompanyModal(true);
  };

  const handleSaveCompany = async () => {
    if (!newCompany.name) {
      toast.error("Le nom de la société est obligatoire");
      return;
    }

    setSavingCompany(true);
    try {
      if (editingCompany) {
        await axios.put(
          `${API}/companies/${editingCompany.id}?name=${encodeURIComponent(newCompany.name)}&google_review_link=${encodeURIComponent(newCompany.google_review_link || "")}&color=${encodeURIComponent(newCompany.color)}`
        );
        toast.success("Société mise à jour");
      } else {
        await axios.post(`${API}/companies`, newCompany);
        toast.success("Société créée avec succès");
      }
      setShowCompanyModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    // Check if any users are assigned to this company
    const usersInCompany = users.filter(u => u.company_ids && u.company_ids.includes(companyId));
    if (usersInCompany.length > 0) {
      toast.error(`Impossible de supprimer: ${usersInCompany.length} utilisateur(s) rattaché(s) à cette société`);
      return;
    }

    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette société ?")) {
      return;
    }

    setDeletingCompanyId(companyId);
    try {
      await axios.delete(`${API}/companies/${companyId}`);
      toast.success("Société supprimée");
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeletingCompanyId(null);
    }
  };

  // Unavailability management functions
  const openCreateUnavailModal = () => {
    setNewUnavail({ user_id: "", start_date: null, end_date: null, reason: "", notes: "" });
    setShowUnavailModal(true);
  };

  const handleSaveUnavail = async () => {
    if (!newUnavail.user_id || !newUnavail.start_date || !newUnavail.end_date || !newUnavail.reason) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSavingUnavail(true);
    try {
      await axios.post(`${API}/unavailabilities`, {
        user_id: newUnavail.user_id,
        start_date: format(newUnavail.start_date, "yyyy-MM-dd"),
        end_date: format(newUnavail.end_date, "yyyy-MM-dd"),
        reason: newUnavail.reason,
        notes: newUnavail.notes || null
      });
      toast.success("Indisponibilité enregistrée");
      setShowUnavailModal(false);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingUnavail(false);
    }
  };

  const handleDeleteUnavail = async (unavailId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette indisponibilité ?")) return;
    
    try {
      await axios.delete(`${API}/unavailabilities/${unavailId}`);
      toast.success("Indisponibilité supprimée");
      fetchDashboardData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      conge: "Congé",
      rtt: "RTT",
      maladie: "Maladie",
      formation: "Formation",
      autre: "Autre"
    };
    return labels[reason] || reason;
  };

  const getReasonColor = (reason) => {
    const colors = {
      conge: "bg-blue-100 text-blue-700",
      rtt: "bg-purple-100 text-purple-700",
      maladie: "bg-rose-100 text-rose-700",
      formation: "bg-emerald-100 text-emerald-700",
      autre: "bg-slate-100 text-slate-700"
    };
    return colors[reason] || "bg-slate-100 text-slate-700";
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "admin":
        return <Shield className="w-4 h-4" />;
      case "teleprospector":
        return <Headphones className="w-4 h-4" />;
      case "sales_rep":
        return <Briefcase className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "admin":
        return <Badge className="bg-purple-100 text-purple-700">Administrateur</Badge>;
      case "teleprospector":
        return <Badge className="bg-blue-100 text-blue-700">Téléprospecteur</Badge>;
      case "sales_rep":
        return <Badge className="bg-emerald-100 text-emerald-700">Commercial</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700">{role}</Badge>;
    }
  };

  const StatCard = ({ title, value, subValue, icon: Icon, color }) => (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">{title}</p>
            <p className={`text-4xl font-bold font-['Manrope'] ${color}`}>{value}</p>
            {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color.replace('text-', 'bg-').replace('-700', '-100')} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Chargement du tableau de bord...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Manrope'] text-slate-900">Administration</h1>
              <p className="text-sm text-slate-500">Tableau de bord Vanessa</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              data-testid="settings-btn"
              className="text-slate-600"
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <span className="text-sm text-slate-600">{user?.name}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              data-testid="logout-btn"
              className="text-slate-500"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-screen-xl mx-auto p-6">
        <Tabs defaultValue="dashboard" className="space-y-6">
          <TabsList>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Tableau de bord</TabsTrigger>
            <TabsTrigger value="companies" data-testid="tab-companies">Sociétés</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-team">Équipe</TabsTrigger>
            <TabsTrigger value="unavailabilities" data-testid="tab-unavailabilities">Indisponibilités</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">Import clients</TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">Export données</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Reminders Alert */}
            {pendingReminders && (pendingReminders.pending_24h_reminders > 0 || pendingReminders.pending_1h_reminders > 0) && (
              <Card className="border-amber-200 bg-amber-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-900">Rappels en attente</p>
                        <p className="text-sm text-amber-700">
                          {pendingReminders.pending_24h_reminders} rappel(s) 24h • {pendingReminders.pending_1h_reminders} rappel(s) 1h
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleSendReminders}
                      disabled={sendingReminders}
                      data-testid="send-reminders-btn"
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {sendingReminders ? (
                        <Clock className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Envoyer les rappels
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Appels effectués"
                value={stats?.calls?.total || 0}
                subValue={`${stats?.calls?.today || 0} aujourd'hui`}
                icon={Phone}
                color="text-indigo-700"
              />
              <StatCard
                title="Rendez-vous"
                value={stats?.appointments?.total || 0}
                subValue={`${stats?.appointments?.completed || 0} terminés`}
                icon={Calendar}
                color="text-emerald-700"
              />
              <StatCard
                title="Avis demandés"
                value={stats?.reviews?.total || 0}
                subValue={`${stats?.reviews?.today || 0} aujourd'hui`}
                icon={Send}
                color="text-amber-700"
              />
              <StatCard
                title="Clients"
                value={stats?.customers?.total || 0}
                subValue="Total en base"
                icon={Users}
                color="text-blue-700"
              />
            </div>

            {/* Satisfaction & NPS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Satisfaction moyenne
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold font-['Manrope'] text-slate-900">
                      {stats?.satisfaction?.average || 0}
                    </span>
                    <span className="text-2xl text-slate-400">/5</span>
                  </div>
                  <div className="flex gap-1 mt-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star
                        key={i}
                        className={`w-6 h-6 ${
                          i <= (stats?.satisfaction?.average || 0)
                            ? "text-amber-400 fill-amber-400"
                            : "text-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-500" />
                    Score NPS moyen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold font-['Manrope'] text-slate-900">
                      {stats?.nps?.average || 0}
                    </span>
                    <span className="text-2xl text-slate-400">/10</span>
                  </div>
                  <div className="mt-3">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          (stats?.nps?.average || 0) >= 9
                            ? "bg-emerald-500"
                            : (stats?.nps?.average || 0) >= 7
                            ? "bg-amber-500"
                            : "bg-rose-500"
                        }`}
                        style={{ width: `${((stats?.nps?.average || 0) / 10) * 100}%` }}
                      />
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      {(stats?.nps?.average || 0) >= 9
                        ? "Promoteurs"
                        : (stats?.nps?.average || 0) >= 7
                        ? "Passifs"
                        : "Détracteurs"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Calls */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Appels récents</CardTitle>
              </CardHeader>
              <CardContent>
                {recentCalls.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucun appel récent</p>
                ) : (
                  <div className="space-y-3">
                    {recentCalls.map((call) => (
                      <div
                        key={call.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-200">
                            <Phone className="w-4 h-4 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{call.customer_name}</p>
                            <p className="text-sm text-slate-500">
                              Par {call.teleprospector_name} • {format(new Date(call.created_at), "d MMM à HH:mm", { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {call.satisfaction_score && (
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                              <span className="text-sm font-medium">{call.satisfaction_score}/5</span>
                            </div>
                          )}
                          {call.nps_score !== null && (
                            <Badge
                              className={`${
                                call.nps_score >= 9
                                  ? "bg-emerald-100 text-emerald-700"
                                  : call.nps_score >= 7
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                              }`}
                            >
                              NPS: {call.nps_score}
                            </Badge>
                          )}
                          {call.has_issues && (
                            <Badge className="bg-rose-100 text-rose-700">
                              Problème signalé
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Companies Tab */}
          <TabsContent value="companies" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Gestion des sociétés</CardTitle>
                  <Button
                    onClick={openCreateCompanyModal}
                    data-testid="add-company-btn"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Ajouter une société
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {companies.length === 0 ? (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">Aucune société créée</p>
                    <p className="text-sm text-slate-400">Créez votre première société pour pouvoir ajouter des utilisateurs</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {companies.map((company) => {
                      const companyUsers = users.filter(u => u.company_ids && u.company_ids.includes(company.id));
                      return (
                        <div
                          key={company.id}
                          className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${company.color}20` }}
                            >
                              <Building2 className="w-5 h-5" style={{ color: company.color }} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{company.name}</p>
                              <p className="text-sm text-slate-500">
                                {companyUsers.length} utilisateur{companyUsers.length > 1 ? "s" : ""} rattaché{companyUsers.length > 1 ? "s" : ""}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {company.google_review_link && (
                              <Badge className="bg-green-100 text-green-700">
                                Lien Google Review
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCompanyModal(company)}
                              className="text-slate-500 hover:text-indigo-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCompany(company.id)}
                              disabled={deletingCompanyId === company.id}
                              className="text-slate-500 hover:text-rose-600"
                            >
                              {deletingCompanyId === company.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info box */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <h4 className="font-medium text-blue-900 mb-2">À propos des sociétés</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Chaque société peut avoir son propre lien Google Review</li>
                  <li>• Les clients importés sont rattachés à une société spécifique</li>
                  <li>• Un commercial est rattaché à une seule société</li>
                  <li>• Une téléprospectrice peut gérer plusieurs sociétés</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          <TabsContent value="team" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Gestion de l'équipe</CardTitle>
                  <Button
                    onClick={openCreateUserModal}
                    data-testid="add-user-btn"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Ajouter un utilisateur
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {users.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">Aucun utilisateur</p>
                ) : (
                  <div className="space-y-3">
                    {users.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            u.role === "admin" ? "bg-purple-100" :
                            u.role === "teleprospector" ? "bg-blue-100" : "bg-emerald-100"
                          }`}>
                            {getRoleIcon(u.role)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                            {u.role !== "admin" && u.company_ids && u.company_ids.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {u.company_ids.map(companyId => {
                                  const company = companies.find(c => c.id === companyId);
                                  return company ? (
                                    <span key={companyId} className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                                      {company.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getRoleBadge(u.role)}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditUserModal(u)}
                              data-testid={`edit-user-${u.id}`}
                              className="text-slate-500 hover:text-indigo-600"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            {u.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(u.id)}
                                disabled={deletingUserId === u.id}
                                data-testid={`delete-user-${u.id}`}
                                className="text-slate-500 hover:text-rose-600"
                              >
                                {deletingUserId === u.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Role descriptions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-purple-900">Administrateur</h4>
                  </div>
                  <p className="text-sm text-purple-700">
                    Accès complet : gestion des utilisateurs, statistiques, import/export, paramètres.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Headphones className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-blue-900">Téléprospecteur</h4>
                  </div>
                  <p className="text-sm text-blue-700">
                    Interface d'appels optimisée, enregistrement des résultats, planification des RDV.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-emerald-900">Commercial</h4>
                  </div>
                  <p className="text-sm text-emerald-700">
                    Vue mobile des RDV du jour, accès GPS, notes de visite, historique client.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Unavailabilities Tab */}
          <TabsContent value="unavailabilities" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <CalendarOff className="w-5 h-5 text-slate-600" />
                    Gestion des indisponibilités
                  </CardTitle>
                  <Button onClick={openCreateUnavailModal} className="bg-indigo-600 hover:bg-indigo-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Nouvelle indisponibilité
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {unavailabilities.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarOff className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Aucune indisponibilité programmée</p>
                    <p className="text-slate-400 text-sm mt-1">Ajoutez les congés, RTT ou absences de vos équipes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {unavailabilities.map((unavail) => (
                      <div key={unavail.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                            {getRoleIcon(users.find(u => u.id === unavail.user_id)?.role)}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{unavail.user_name}</p>
                            <p className="text-sm text-slate-500">
                              {format(new Date(unavail.start_date), "d MMM", { locale: fr })} - {format(new Date(unavail.end_date), "d MMM yyyy", { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getReasonColor(unavail.reason)}>{getReasonLabel(unavail.reason)}</Badge>
                          {unavail.notes && (
                            <span className="text-xs text-slate-500 max-w-[200px] truncate">{unavail.notes}</span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUnavail(unavail.id)}
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <CalendarOff className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900">Impact sur les RDV</p>
                    <p className="text-sm text-blue-700">
                      Quand un commercial est marqué indisponible, les téléprospecteurs ne pourront pas lui programmer de rendez-vous sur ces dates.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Importer des clients</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Company Selection - Required before import */}
                <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200">
                  <Label className="text-indigo-900 font-medium mb-3 block">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Sélectionnez la société pour cet import *
                  </Label>
                  {companies.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-2">
                      <p className="text-amber-700 text-sm">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        Aucune société créée. Veuillez d'abord créer une société dans l'onglet "Sociétés".
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-3">
                      {companies.map((company) => (
                        <div
                          key={company.id}
                          onClick={() => setSelectedImportCompany(company.id)}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            selectedImportCompany === company.id
                              ? "border-indigo-500 bg-white shadow-md"
                              : "border-slate-200 bg-white hover:border-slate-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-8 h-8 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: `${company.color}20` }}
                            >
                              {selectedImportCompany === company.id ? (
                                <Check className="w-4 h-4" style={{ color: company.color }} />
                              ) : (
                                <Building2 className="w-4 h-4" style={{ color: company.color }} />
                              )}
                            </div>
                            <span className={`font-medium ${
                              selectedImportCompany === company.id ? "text-indigo-700" : "text-slate-700"
                            }`}>
                              {company.name}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedImportCompany && (
                    <p className="text-sm text-indigo-600 mt-3">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      Les clients importés seront rattachés à : <strong>{companies.find(c => c.id === selectedImportCompany)?.name}</strong>
                    </p>
                  )}
                </div>

                {/* Upload Area */}
                <div className={`bg-slate-50 rounded-xl p-6 border border-dashed ${
                  selectedImportCompany ? "border-slate-300" : "border-slate-200 opacity-60"
                }`}>
                  <div className="text-center">
                    <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="font-medium text-slate-900 mb-2">
                      Importer un fichier CSV ou Excel
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Format accepté: CSV, XLSX, XLS
                    </p>
                    <div className="flex justify-center gap-3">
                      <Button
                        variant="outline"
                        onClick={downloadTemplate}
                        data-testid="download-template-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger le modèle
                      </Button>
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing || !selectedImportCompany || companies.length === 0}
                        data-testid="upload-file-btn"
                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {importing ? "Import en cours..." : "Choisir un fichier"}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileUpload}
                        className="hidden"
                        data-testid="file-input"
                      />
                    </div>
                    {!selectedImportCompany && companies.length > 0 && (
                      <p className="text-sm text-amber-600 mt-4">
                        ⚠️ Sélectionnez une société ci-dessus avant d'importer
                      </p>
                    )}
                  </div>
                </div>

                {/* Import Result */}
                {importResult && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-slate-900">
                      Résultat de l'import 
                      {importResult.companyName && (
                        <span className="text-indigo-600 ml-2">
                          pour {importResult.companyName}
                        </span>
                      )}
                    </h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-emerald-50 rounded-lg p-4 text-center">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-emerald-700">{importResult.imported}</p>
                        <p className="text-sm text-emerald-600">Importés</p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-amber-700">{importResult.duplicates}</p>
                        <p className="text-sm text-amber-600">Doublons</p>
                      </div>
                      <div className="bg-rose-50 rounded-lg p-4 text-center">
                        <XCircle className="w-6 h-6 text-rose-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-rose-700">{importResult.total_errors}</p>
                        <p className="text-sm text-rose-600">Erreurs</p>
                      </div>
                    </div>

                    {importResult.errors.length > 0 && (
                      <div className="bg-rose-50 rounded-lg p-4">
                        <p className="font-medium text-rose-700 mb-2">Erreurs détectées:</p>
                        <ul className="text-sm text-rose-600 space-y-1">
                          {importResult.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Template Info */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Colonnes acceptées</h4>
                  <p className="text-sm text-blue-700 mb-3">
                    Vous pouvez utiliser les noms français ou anglais pour vos colonnes.
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">nom / name</Badge>
                      <span className="text-blue-700">Nom du client *</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">telephone / phone</Badge>
                      <span className="text-blue-700">Téléphone *</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">ville / city</Badge>
                      <span className="text-blue-700">Ville *</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">type_travaux / job_type</Badge>
                      <span className="text-blue-700">Type de travaux *</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">email</Badge>
                      <span className="text-slate-600">Email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">adresse / address</Badge>
                      <span className="text-slate-600">Adresse</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">date_bdc</Badge>
                      <span className="text-slate-600">Date BDC</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">somme_ca</Badge>
                      <span className="text-slate-600">Montant CA</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">origine</Badge>
                      <span className="text-slate-600">Origine</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600">commercial</Badge>
                      <span className="text-slate-600">Commercial</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-6">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Exporter les données</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-slate-500">
                  Exportez vos données au format CSV pour les analyser dans Excel ou un autre logiciel de tableur.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Export Customers */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                        <Users className="w-7 h-7 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">Clients</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Export de tous les clients avec leurs informations de contact
                      </p>
                      <Button
                        onClick={() => handleExport("customers")}
                        disabled={exporting.customers}
                        data-testid="export-customers-btn"
                        className="w-full bg-blue-600 hover:bg-blue-700"
                      >
                        {exporting.customers ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileDown className="w-4 h-4 mr-2" />
                        )}
                        {exporting.customers ? "Export en cours..." : "Exporter les clients"}
                      </Button>
                    </div>
                  </div>

                  {/* Export Calls */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                        <Phone className="w-7 h-7 text-indigo-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">Appels</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Historique complet des appels avec satisfaction et NPS
                      </p>
                      <Button
                        onClick={() => handleExport("calls")}
                        disabled={exporting.calls}
                        data-testid="export-calls-btn"
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                      >
                        {exporting.calls ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileDown className="w-4 h-4 mr-2" />
                        )}
                        {exporting.calls ? "Export en cours..." : "Exporter les appels"}
                      </Button>
                    </div>
                  </div>

                  {/* Export Appointments */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="text-center">
                      <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                        <Calendar className="w-7 h-7 text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-2">Rendez-vous</h3>
                      <p className="text-sm text-slate-500 mb-4">
                        Tous les RDV avec détails clients et commerciaux
                      </p>
                      <Button
                        onClick={() => handleExport("appointments")}
                        disabled={exporting.appointments}
                        data-testid="export-appointments-btn"
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        {exporting.appointments ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <FileDown className="w-4 h-4 mr-2" />
                        )}
                        {exporting.appointments ? "Export en cours..." : "Exporter les RDV"}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Export Info */}
                <div className="bg-blue-50 rounded-lg p-4 mt-6">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Format d'export
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Les fichiers sont exportés au format CSV (compatible Excel)</li>
                    <li>• L'encodage est UTF-8 pour conserver les accents</li>
                    <li>• Le nom du fichier inclut la date et l'heure d'export</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Paramètres</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Lien Google Review</Label>
              <Input
                value={googleReviewLink}
                onChange={(e) => setGoogleReviewLink(e.target.value)}
                data-testid="google-review-link-input"
                placeholder="https://g.page/r/..."
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ce lien sera envoyé aux clients satisfaits pour laisser un avis
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveSettings} data-testid="save-settings-btn" className="bg-indigo-600 hover:bg-indigo-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Modal */}
      <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingUser ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nom complet *</Label>
              <Input
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="user-name-input"
                placeholder="Jean Dupont"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-700">Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="user-email-input"
                placeholder="jean@exemple.com"
                className="mt-1"
                disabled={!!editingUser}
              />
              {editingUser && (
                <p className="text-xs text-slate-500 mt-1">L'email ne peut pas être modifié</p>
              )}
            </div>
            <div>
              <Label className="text-slate-700">
                Mot de passe {editingUser ? "(laisser vide pour ne pas changer)" : "*"}
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="user-password-input"
                  placeholder="••••••••"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Rôle *</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value, company_ids: [] })}
              >
                <SelectTrigger data-testid="user-role-select" className="mt-1">
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teleprospector">
                    <div className="flex items-center gap-2">
                      <Headphones className="w-4 h-4 text-blue-600" />
                      Téléprospecteur
                    </div>
                  </SelectItem>
                  <SelectItem value="sales_rep">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-emerald-600" />
                      Commercial
                    </div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      Administrateur
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Company selection - only for non-admin roles */}
            {newUser.role && newUser.role !== "admin" && (
              <div>
                <Label className="text-slate-700">
                  {newUser.role === "sales_rep" ? "Société *" : "Société(s) *"}
                </Label>
                <p className="text-xs text-slate-500 mb-2">
                  {newUser.role === "sales_rep" 
                    ? "Un commercial est rattaché à une seule société" 
                    : "Une téléprospectrice peut être rattachée à plusieurs sociétés"}
                </p>
                {companies.length === 0 ? (
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                    Aucune société créée. Veuillez d'abord créer une société.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {companies.map((company) => {
                      const isSelected = (newUser.company_ids || []).includes(company.id);
                      return (
                        <div
                          key={company.id}
                          onClick={() => handleCompanyToggle(company.id)}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected 
                              ? "bg-indigo-50 border border-indigo-200" 
                              : "bg-slate-50 border border-transparent hover:bg-slate-100"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isSelected ? "bg-indigo-100" : "bg-slate-200"
                          }`}>
                            {isSelected ? (
                              <Check className="w-4 h-4 text-indigo-600" />
                            ) : (
                              <Building2 className="w-4 h-4 text-slate-500" />
                            )}
                          </div>
                          <span className={`font-medium ${isSelected ? "text-indigo-700" : "text-slate-700"}`}>
                            {company.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {newUser.role === "admin" && (
              <div className="bg-purple-50 rounded-lg p-3">
                <p className="text-sm text-purple-700">
                  <Shield className="w-4 h-4 inline mr-1" />
                  Un administrateur a accès à toutes les sociétés
                </p>
              </div>
            )}

            {/* Send email option - only for new users */}
            {!editingUser && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={newUser.send_email}
                  onChange={(e) => setNewUser({ ...newUser, send_email: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                />
                <label htmlFor="send_email" className="text-sm text-blue-700 cursor-pointer">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Envoyer les identifiants par email au collaborateur
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={savingUser}
              data-testid="save-user-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : editingUser ? (
                "Mettre à jour"
              ) : (
                "Créer l'utilisateur"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Modal */}
      <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              {editingCompany ? "Modifier la société" : "Ajouter une société"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nom de la société *</Label>
              <Input
                value={newCompany.name}
                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                data-testid="company-name-input"
                placeholder="Ma Société"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-700">Lien Google Review</Label>
              <Input
                value={newCompany.google_review_link}
                onChange={(e) => setNewCompany({ ...newCompany, google_review_link: e.target.value })}
                data-testid="company-review-link-input"
                placeholder="https://g.page/r/..."
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ce lien sera utilisé pour envoyer les demandes d'avis aux clients de cette société
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Couleur</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  value={newCompany.color}
                  onChange={(e) => setNewCompany({ ...newCompany, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border border-slate-200"
                />
                <span className="text-sm text-slate-500">Couleur d'identification de la société</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompanyModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSaveCompany}
              disabled={savingCompany}
              data-testid="save-company-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingCompany ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : editingCompany ? (
                "Mettre à jour"
              ) : (
                "Créer la société"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unavailability Modal */}
      <Dialog open={showUnavailModal} onOpenChange={setShowUnavailModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarOff className="w-5 h-5" />
              Nouvelle indisponibilité
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Collaborateur *</Label>
              <Select value={newUnavail.user_id} onValueChange={(v) => setNewUnavail({...newUnavail, user_id: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role !== 'admin').map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.role === 'sales_rep' ? 'Commercial' : 'Téléprospecteur'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Date début *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start">
                      <Calendar className="mr-2 h-4 w-4" />
                      {newUnavail.start_date ? format(newUnavail.start_date, "d MMM", { locale: fr }) : "Choisir"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarPicker 
                      mode="single" 
                      selected={newUnavail.start_date} 
                      onSelect={(d) => setNewUnavail({...newUnavail, start_date: d})} 
                      initialFocus 
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-slate-700">Date fin *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full mt-1 justify-start">
                      <Calendar className="mr-2 h-4 w-4" />
                      {newUnavail.end_date ? format(newUnavail.end_date, "d MMM", { locale: fr }) : "Choisir"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarPicker 
                      mode="single" 
                      selected={newUnavail.end_date} 
                      onSelect={(d) => setNewUnavail({...newUnavail, end_date: d})}
                      disabled={(date) => newUnavail.start_date && date < newUnavail.start_date}
                      initialFocus 
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div>
              <Label className="text-slate-700">Motif *</Label>
              <Select value={newUnavail.reason} onValueChange={(v) => setNewUnavail({...newUnavail, reason: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionnez" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conge">Congé</SelectItem>
                  <SelectItem value="rtt">RTT</SelectItem>
                  <SelectItem value="maladie">Maladie</SelectItem>
                  <SelectItem value="formation">Formation</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700">Notes (optionnel)</Label>
              <Input
                value={newUnavail.notes}
                onChange={(e) => setNewUnavail({...newUnavail, notes: e.target.value})}
                placeholder="Ex: Vacances d'été"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnavailModal(false)}>Annuler</Button>
            <Button onClick={handleSaveUnavail} disabled={savingUnavail} className="bg-indigo-600 hover:bg-indigo-700">
              {savingUnavail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
